import { ApolloClient } from "@apollo/client/core";
import { LoadStatus } from "../types/component-configs";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  EMAIL_VERIFICATION_CACHE_KEY,
  OAUTH_TYPE_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  TOKEN_CACHE_KEY,
} from "../constants/login";
import {
  CachedEmailVerificationData,
  CachedRefreshTokenData,
  CachedTokenData,
} from "../types/app-types";
import {
  clearCacheData,
  CacheData,
  readFromCache,
  writeToCache,
  attachListeners,
  removeListeners,
  removeFromCache,
} from "./cache-listeners";
import {
  clearNativeStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemInLocalStorage,
} from "./storage-helpers";
import { REFRESH_TOKEN_PROGRESS_CACHE_KEY } from "../constants/login";
import { generateRandomNumber } from "./number-helpers";

type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

type FetchAuthTokens = {
  [Property in keyof AuthTokens]?: AuthTokens[Property];
};

export function getTokenFromNativeStorage(): FetchAuthTokens {
  return {
    accessToken: getItemFromLocalStorage(ACCESS_TOKEN_STORAGE_KEY) || undefined,
    refreshToken:
      getItemFromLocalStorage(REFRESH_TOKEN_STORAGE_KEY) || undefined,
  };
}

export function deleteTokenFromNativeStorage() {
  removeItemFromLocalStorage(ACCESS_TOKEN_STORAGE_KEY);
  removeItemFromLocalStorage(REFRESH_TOKEN_STORAGE_KEY);
}

export function getTokenDataFromCachedData(
  cachedData?: CacheData<CachedTokenData>
) {
  if (cachedData && cachedData.data) {
    const { data } = cachedData;
    return data.token;
  }
}

function getTokenFromCache() {
  return getTokenDataFromCachedData(
    readFromCache({
      cacheKey: TOKEN_CACHE_KEY,
    })
  );
}

export function getEmailVerificationFromCache() {
  return readFromCache<CachedEmailVerificationData>({
    cacheKey: EMAIL_VERIFICATION_CACHE_KEY,
  })?.data?.emailVerification;
}

/**
 * Allow the API to proceed with the token it has so that the token
 * can be refreshed even while waiting for email verification
 */
function getTokenForApi() {
  const token = getTokenFromCache();
  if (token) {
    return token;
  }

  const { accessToken } = getTokenFromNativeStorage();
  return accessToken;
}

/**
 * If email verification is in progress, then deem the token unusable
 * so that the entire app behaves as if auth doesn't exist. Each page
 * should use the token custom hook to manage the various stages
 *
 * Right now refresh token first time check is not added since its not
 * needed. If this method is used at start-up then deem the token unusable
 * till that check completes
 *
 * @returns auth token for the app to determine login based actions
 */
export function getToken() {
  const emailVerification = getEmailVerificationFromCache();
  // Token is only usable for the app if there's no email verification in progress
  if (emailVerification) {
    return undefined;
  }

  return getTokenForApi();
}

export function getTokenForApiRequest(operationKey?: string) {
  // Random key is added to ensure uniqueness in case multiple requests are made
  // at the same millisecond
  const keyParts: Array<string> = [
    Date.now().toString(),
    generateRandomNumber(10).toString(),
  ];
  if (operationKey) {
    keyParts.push(operationKey);
  }
  const clientKey = keyParts.join("-");
  return new Promise((resolve) => {
    function cacheChangeHandler(cachedData?: CacheData) {
      if (cachedData?.loadStatus !== LoadStatus.PROGRESS) {
        const token = getTokenForApi();
        resolve(token);
      }
    }

    attachListeners(
      {
        cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
        clientKey,
      },
      { cacheChangeHandler }
    );
    cacheChangeHandler(
      readFromCache({
        cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
      })
    );
  }).then((token) => {
    removeListeners({
      cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
      clientKey,
    });
    return token;
  });
}

function isTokenValid(token: string) {
  return token.trim() !== "";
}

type SaveTokenToNativeStorageParams = AuthTokens;
function saveTokenToNativeStorage(tokenData: SaveTokenToNativeStorageParams) {
  const { accessToken, refreshToken } = tokenData;
  if (!isTokenValid(accessToken) || !isTokenValid(refreshToken)) {
    return;
  }

  setItemInLocalStorage(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  setItemInLocalStorage(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
}

export function saveTokenInCache(token: string) {
  if (!isTokenValid(token)) {
    return;
  }

  writeToCache<CachedTokenData>({
    cacheKey: TOKEN_CACHE_KEY,
    data: {
      token,
    },
  });
}

function saveEmailVerificationInCache(emailVerification: boolean) {
  writeToCache<CachedEmailVerificationData>({
    cacheKey: EMAIL_VERIFICATION_CACHE_KEY,
    data: {
      emailVerification,
    },
  });
}

function removeEmailVerificationFromCache() {
  removeFromCache({ cacheKey: EMAIL_VERIFICATION_CACHE_KEY });
}

type SaveTokenDetailsParams = AuthTokens;
function saveTokenDetails(tokenData: SaveTokenDetailsParams) {
  saveTokenToNativeStorage(tokenData);
  saveTokenInCache(tokenData.accessToken);
}

export function saveOauthTypeDetails(type?: string) {
  if (!type) {
    return;
  }

  setItemInLocalStorage(OAUTH_TYPE_STORAGE_KEY, type);
}

export function saveEmailVerificationDetails(emailVerification?: boolean) {
  if (!emailVerification) {
    return;
  }

  saveEmailVerificationInCache(emailVerification);
}

export function removeEmailVerificationDetails() {
  removeEmailVerificationFromCache();
}

type LoginUserParams = SaveTokenDetailsParams & {
  authType?: string;
  emailVerification?: boolean;
};
export function completeUserLogin({
  accessToken,
  refreshToken,
  authType,
  emailVerification,
}: LoginUserParams) {
  saveTokenDetails({ accessToken, refreshToken });
  saveOauthTypeDetails(authType);
  saveEmailVerificationDetails(emailVerification);
}

type CompleteUserLogoutConfigParam = {
  autoLogout?: boolean;
};
export function completeUserLogout(
  client: ApolloClient<unknown>,
  { autoLogout }: CompleteUserLogoutConfigParam = {}
) {
  client.clearStore();
  clearNativeStorage();
  clearCacheData({ notifyAllListeners: autoLogout });
}

type CompleteRefreshTokenParams = SaveTokenDetailsParams & {
  emailVerification?: boolean;
};
export function completeRefreshToken({
  accessToken,
  refreshToken,
  emailVerification,
}: CompleteRefreshTokenParams) {
  saveTokenDetails({ accessToken, refreshToken });
  saveEmailVerificationDetails(emailVerification);
}

export function completeEmailVerification() {
  removeEmailVerificationDetails();
}

type RefreshTokenCheckProgressParams = {
  status: string;
};
export function refreshTokenCheckProgress({
  status,
}: RefreshTokenCheckProgressParams) {
  const refreshTokenData: Partial<CachedRefreshTokenData> = {};
  if (status === LoadStatus.SUCCESS || status === LoadStatus.ERROR) {
    refreshTokenData.firstCheck = false;
  } else if (status === LoadStatus.PROGRESS) {
    const cachedData = readFromCache<CachedRefreshTokenData>({
      cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
    });
    // Nothing in cache as of now, consider it a first time check
    if (!cachedData?.loadStatus) {
      refreshTokenData.firstCheck = true;
    }
  }

  writeToCache<CachedRefreshTokenData>({
    cacheKey: REFRESH_TOKEN_PROGRESS_CACHE_KEY,
    data: refreshTokenData,
    status,
  });
}
